import { useState, useCallback, useEffect, useRef } from 'react';

interface UseTextToSpeechReturn {
  isSpeaking: boolean;
  speak: (text: string, onEnd?: () => void) => void;
  stop: () => void;
  isSupported: boolean;
}

function cleanTextForSpeech(text: string): string {
  let cleaned = text;

  // Remove markdown headers
  cleaned = cleaned.replace(/#{1,6}\s+/g, '');
  // Remove bold/italic markers
  cleaned = cleaned.replace(/\*{1,3}([^*]+)\*{1,3}/g, '$1');
  cleaned = cleaned.replace(/_{1,3}([^_]+)_{1,3}/g, '$1');
  // Remove strikethrough
  cleaned = cleaned.replace(/~~([^~]+)~~/g, '$1');
  // Remove inline code
  cleaned = cleaned.replace(/`([^`]+)`/g, '$1');
  // Remove code blocks
  cleaned = cleaned.replace(/```[\s\S]*?```/g, '');
  // Remove links but keep text: [text](url) -> text
  cleaned = cleaned.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
  // Remove raw URLs
  cleaned = cleaned.replace(/https?:\/\/[^\s]+/g, '');
  // Remove bullet points
  cleaned = cleaned.replace(/^\s*[-*+]\s/gm, '');
  // Remove numbered lists prefix
  cleaned = cleaned.replace(/^\s*\d+\.\s/gm, '');
  // Remove blockquotes
  cleaned = cleaned.replace(/^>\s?/gm, '');
  // Remove horizontal rules
  cleaned = cleaned.replace(/^---+$/gm, '');
  // Remove document emojis
  cleaned = cleaned.replace(/[📄📋📁📂🗂️📑📃📝✅❌⚠️🔗💡]/g, '');
  // Expand common legal abbreviations
  cleaned = cleaned.replace(/\bart\.\s?/gi, 'artículo ');
  cleaned = cleaned.replace(/\barts\.\s?/gi, 'artículos ');
  cleaned = cleaned.replace(/\bnum\.\s?/gi, 'número ');
  cleaned = cleaned.replace(/\bpág\.\s?/gi, 'página ');
  // Clean up extra whitespace
  cleaned = cleaned.replace(/\n{3,}/g, '\n\n');
  cleaned = cleaned.replace(/\s{2,}/g, ' ');
  cleaned = cleaned.trim();

  return cleaned;
}

export function useTextToSpeech(): UseTextToSpeechReturn {
  const [isSpeaking, setIsSpeaking] = useState(false);
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);
  const isSupported = typeof window !== 'undefined' && 'speechSynthesis' in window;

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (isSupported) {
        window.speechSynthesis.cancel();
      }
    };
  }, [isSupported]);

  const getSpanishVoice = useCallback((): SpeechSynthesisVoice | null => {
    const voices = window.speechSynthesis.getVoices();
    const spanishVoices = voices.filter(v => v.lang.startsWith('es'));
    if (spanishVoices.length === 0) return null;
    const premium = spanishVoices.find(v =>
      /google|microsoft|natural|neural|enhanced/i.test(v.name)
    );
    if (premium) return premium;
    const esES = spanishVoices.find(v => v.lang === 'es-ES');
    if (esES) return esES;
    const notLocal = spanishVoices.find(v => !v.localService);
    if (notLocal) return notLocal;
    return spanishVoices[0];
  }, []);

  const speak = useCallback((text: string, onEnd?: () => void) => {
    if (!isSupported) return;

    window.speechSynthesis.cancel();

    const cleanedText = cleanTextForSpeech(text);
    if (!cleanedText) return;

    const utterance = new SpeechSynthesisUtterance(cleanedText);
    utteranceRef.current = utterance;

    const voice = getSpanishVoice();
    if (voice) utterance.voice = voice;
    utterance.lang = 'es-ES';
    utterance.rate = 1.05;
    utterance.pitch = 1.0;

    utterance.onstart = () => setIsSpeaking(true);
    utterance.onend = () => {
      setIsSpeaking(false);
      onEnd?.();
    };
    utterance.onerror = () => {
      setIsSpeaking(false);
    };

    window.speechSynthesis.speak(utterance);
  }, [isSupported, getSpanishVoice]);

  const stop = useCallback(() => {
    if (!isSupported) return;
    window.speechSynthesis.cancel();
    setIsSpeaking(false);
  }, [isSupported]);

  return { isSpeaking, speak, stop, isSupported };
}
